import { h as __h } from "preact";
import { computed } from "@preact/signals";
import { adminRolesResponse } from "~/backend/validate.js";
/** @import { adminEnterpriseRoleResponse, adminOrgRoleResponse } from "~/backend/validate.js"; */
import { query, response } from "~/lib/api.js";
import html from "~/util/html.js";
import { confirmModal } from "~/component/ConfirmModal.js";

/** @typedef {"org" | "enterprise"} RoleScope */
/**
 * @typedef {object} Role
 * @property {string} key
 * @property {string} label
 * @property {string} description
 * @property {boolean} is_danger
 */

export default class Roles {
  static #query = query(() => ({
    key: ["/ui/admin/roles"]
  }), {
    schema: response(adminRolesResponse)
  });
  static #org = computed(() => Roles.#query.data?.data.org.roles.map(Roles.#toRole) ?? []);
  static #enterprise = computed(() => Roles.#query.data?.data.enterprise.roles.map(Roles.#toRole) ?? []);
  static get ready() {
    return Roles.#query.data !== undefined;
  }
  static get error() {
    return Roles.#query.error;
  }

  /** @returns {Role[]} */
  static get org() {
    return Roles.#org.value;
  }
  /** @returns {Role[]} */
  static get enterprise() {
    return Roles.#enterprise.value;
  }

  /**
   * The role's display name, or its key until the roles have loaded.
   *
   * @param {RoleScope} scope
   * @param {string} key
   */
  static label(scope, key) {
    const roles = scope === "org" ? Roles.org : Roles.enterprise;
    return roles.find(role => role.key === key)?.label ?? key;
  }

  /**
   * Asks for confirmation before granting a dangerous role. Resolves true when the change should
   * proceed.
   *
   * @param {Role} role
   * @param {string} title
   */
  static async confirm(role, title) {
    if (!role.is_danger) return true;
    const confirm = await confirmModal({
      title,
      action: "Change role",
      kind: "primary",
      body: __h("p", {}, role.description)
    });
    return confirm === true;
  }

  /**
   * @param {adminOrgRoleResponse | adminEnterpriseRoleResponse} role
   * @returns {Role}
   */
  static #toRole(role) {
    return {
      key: role.key,
      label: role.name,
      description: role.description,
      is_danger: role.key === "admin"
    };
  }
}